/**
 * MetricsService - Real-time WebRTC and Audio Quality Monitoring
 *
 * Collects and processes metrics for:
 * - Connection quality (RTT, packet loss, jitter, bandwidth)
 * - Audio quality (levels, noise floor, clipping)
 * - Video quality (resolution, framerate, dropped frames)
 * - Recording health (buffer status, sync drift)
 */

// ============================================================================
// Types
// ============================================================================

export type QualityLevel = 'good' | 'fair' | 'poor' | 'unknown';

export interface ConnectionMetrics {
  // Network stats
  rtt: number;                    // Round-trip time in ms
  packetLoss: number;             // Percentage (0-100)
  jitter: number;                 // Jitter in ms
  availableBandwidth: number;     // Estimated available bandwidth in kbps

  // Quality assessment
  quality: QualityLevel;
  qualityLabel: string;           // "Strong", "Fair", "Poor", "Connecting..."

  // Raw stats for expanded view
  bytesReceived: number;
  bytesSent: number;
  packetsLost: number;
  packetsReceived: number;
  packetsSent: number;

  // Timestamps
  timestamp: number;
}

export interface AudioMetrics {
  // Levels (0-100)
  inputLevel: number;             // Current mic input level
  peakLevel: number;              // Peak level in last interval
  noiseFloor: number;             // Estimated noise floor

  // Quality flags
  isClipping: boolean;            // Audio is clipping
  isMuted: boolean;               // Mic is muted
  isSilent: boolean;              // No audio detected

  // Quality assessment
  quality: QualityLevel;
  qualityLabel: string;           // "Clean", "Noisy", "Clipping", "Silent"

  timestamp: number;
}

export interface VideoMetrics {
  // Resolution
  width: number;
  height: number;
  frameRate: number;

  // Quality
  droppedFrames: number;
  totalFrames: number;
  dropRate: number;               // Percentage

  // Encoding
  encodedBitrate: number;         // kbps

  quality: QualityLevel;
  timestamp: number;
}

export interface RecordingMetrics {
  // Buffer health
  bufferLevel: number;            // 0-100, how full the local buffer is
  bufferedDuration: number;       // Seconds of audio buffered

  // Sync
  driftMs: number;                // Audio/video drift in ms

  // Upload status (for cloud backup)
  uploadProgress: number;         // 0-100
  uploadSpeed: number;            // kbps
  pendingChunks: number;

  // Health
  isHealthy: boolean;
  healthLabel: string;

  timestamp: number;
}

export interface AllMetrics {
  connection: ConnectionMetrics;
  audio: AudioMetrics;
  video: VideoMetrics;
  recording: RecordingMetrics;
  overallHealth: QualityLevel;
  timestamp: number;
}

// ============================================================================
// Thresholds (based on industry standards)
// ============================================================================

const THRESHOLDS = {
  connection: {
    rtt: {
      good: 150,      // < 150ms
      fair: 400,      // < 400ms
      // > 400ms = poor
    },
    packetLoss: {
      good: 1,        // < 1%
      fair: 5,        // < 5%
      // > 5% = poor
    },
    jitter: {
      good: 20,       // < 20ms
      fair: 50,       // < 50ms
      // > 50ms = poor
    },
  },
  audio: {
    clippingThreshold: 95,     // Level above this = clipping
    silenceThreshold: 5,       // Level below this = silent
    noiseFloorMax: 20,         // Noise floor above this = noisy
  },
  video: {
    dropRateGood: 1,           // < 1% dropped
    dropRateFair: 5,           // < 5% dropped
  },
};

// ============================================================================
// MetricsService Class
// ============================================================================

type MetricsCallback = (metrics: AllMetrics) => void;

class MetricsService {
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private audioStream: MediaStream | null = null;
  private peerConnection: RTCPeerConnection | null = null;

  private pollingInterval: ReturnType<typeof setInterval> | null = null;
  private callbacks: Set<MetricsCallback> = new Set();

  private lastMetrics: AllMetrics | null = null;
  private audioDataArray: Uint8Array | null = null;

  // For calculating deltas
  private lastStats: {
    bytesReceived: number;
    bytesSent: number;
    packetsReceived: number;
    packetsSent: number;
    packetsLost: number;
    timestamp: number;
  } | null = null;

  // Peak tracking
  private peakLevel = 0;
  private peakResetInterval: ReturnType<typeof setInterval> | null = null;

  /**
   * Initialize the metrics service with media streams
   */
  initialize(audioStream: MediaStream, peerConnection?: RTCPeerConnection): void {
    console.log('[MetricsService] Initializing...');

    this.audioStream = audioStream;
    this.peerConnection = peerConnection || null;

    // Set up audio analysis
    this.setupAudioAnalysis(audioStream);

    // Reset peak level every 2 seconds
    this.peakResetInterval = setInterval(() => {
      this.peakLevel = 0;
    }, 2000);
  }

  /**
   * Set up audio context and analyser for level monitoring
   */
  private setupAudioAnalysis(stream: MediaStream): void {
    try {
      this.audioContext = new AudioContext();
      this.analyser = this.audioContext.createAnalyser();
      this.analyser.fftSize = 256;
      this.analyser.smoothingTimeConstant = 0.3;

      const source = this.audioContext.createMediaStreamSource(stream);
      source.connect(this.analyser);

      // Don't connect to destination (we don't want to hear ourselves)
      this.audioDataArray = new Uint8Array(this.analyser.frequencyBinCount);

      console.log('[MetricsService] Audio analysis initialized');
    } catch (error) {
      console.error('[MetricsService] Failed to set up audio analysis:', error);
    }
  }

  /**
   * Update the peer connection reference (useful when VideoSDK creates new connections)
   */
  setPeerConnection(pc: RTCPeerConnection): void {
    this.peerConnection = pc;
    console.log('[MetricsService] Peer connection updated');
  }

  /**
   * Start collecting metrics at specified interval
   */
  startPolling(intervalMs = 1000): void {
    if (this.pollingInterval) {
      this.stopPolling();
    }

    console.log(`[MetricsService] Starting metrics polling every ${intervalMs}ms`);

    this.pollingInterval = setInterval(async () => {
      const metrics = await this.collectMetrics();
      this.lastMetrics = metrics;

      // Notify all subscribers
      this.callbacks.forEach(callback => {
        try {
          callback(metrics);
        } catch (error) {
          console.error('[MetricsService] Callback error:', error);
        }
      });
    }, intervalMs);
  }

  /**
   * Stop polling
   */
  stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
      console.log('[MetricsService] Polling stopped');
    }
  }

  /**
   * Subscribe to metrics updates
   */
  subscribe(callback: MetricsCallback): () => void {
    this.callbacks.add(callback);

    // Return unsubscribe function
    return () => {
      this.callbacks.delete(callback);
    };
  }

  /**
   * Get the last collected metrics (useful for one-time reads)
   */
  getLastMetrics(): AllMetrics | null {
    return this.lastMetrics;
  }

  /**
   * Collect all metrics
   */
  async collectMetrics(): Promise<AllMetrics> {
    const timestamp = Date.now();

    const [connection, audio, video] = await Promise.all([
      this.collectConnectionMetrics(),
      this.collectAudioMetrics(),
      this.collectVideoMetrics(),
    ]);

    const recording = this.collectRecordingMetrics();

    // Calculate overall health
    const overallHealth = this.calculateOverallHealth(connection, audio, video, recording);

    return {
      connection,
      audio,
      video,
      recording,
      overallHealth,
      timestamp,
    };
  }

  /**
   * Collect connection/network metrics from WebRTC stats
   */
  private async collectConnectionMetrics(): Promise<ConnectionMetrics> {
    const defaultMetrics: ConnectionMetrics = {
      rtt: 0,
      packetLoss: 0,
      jitter: 0,
      availableBandwidth: 0,
      quality: 'unknown',
      qualityLabel: 'Connecting...',
      bytesReceived: 0,
      bytesSent: 0,
      packetsLost: 0,
      packetsReceived: 0,
      packetsSent: 0,
      timestamp: Date.now(),
    };

    if (!this.peerConnection) {
      return defaultMetrics;
    }

    try {
      const stats = await this.peerConnection.getStats();
      let rtt = 0;
      let jitter = 0;
      let packetsLost = 0;
      let packetsReceived = 0;
      let packetsSent = 0;
      let bytesReceived = 0;
      let bytesSent = 0;

      stats.forEach(report => {
        // Get RTT from candidate-pair
        if (report.type === 'candidate-pair' && report.state === 'succeeded') {
          rtt = report.currentRoundTripTime ? report.currentRoundTripTime * 1000 : 0;
          bytesReceived = report.bytesReceived || 0;
          bytesSent = report.bytesSent || 0;
        }

        // Get jitter and packet loss from inbound-rtp
        if (report.type === 'inbound-rtp' && report.kind === 'audio') {
          jitter = report.jitter ? report.jitter * 1000 : 0;
          packetsLost = report.packetsLost || 0;
          packetsReceived = report.packetsReceived || 0;
        }

        // Get outbound stats
        if (report.type === 'outbound-rtp') {
          packetsSent += report.packetsSent || 0;
        }
      });

      // Calculate packet loss percentage
      const totalPackets = packetsReceived + packetsLost;
      const packetLoss = totalPackets > 0 ? (packetsLost / totalPackets) * 100 : 0;

      // Calculate bandwidth (simplified - would need time delta for accurate calculation)
      let availableBandwidth = 0;
      if (this.lastStats) {
        const timeDelta = (Date.now() - this.lastStats.timestamp) / 1000;
        if (timeDelta > 0) {
          const bytesDelta = (bytesReceived - this.lastStats.bytesReceived) +
                            (bytesSent - this.lastStats.bytesSent);
          availableBandwidth = (bytesDelta * 8) / timeDelta / 1000; // kbps
        }
      }

      // Update last stats for next calculation
      this.lastStats = {
        bytesReceived,
        bytesSent,
        packetsReceived,
        packetsSent,
        packetsLost,
        timestamp: Date.now(),
      };

      // Determine quality level
      const quality = this.assessConnectionQuality(rtt, packetLoss, jitter);
      const qualityLabel = this.getConnectionQualityLabel(quality);

      return {
        rtt: Math.round(rtt),
        packetLoss: Math.round(packetLoss * 10) / 10,
        jitter: Math.round(jitter),
        availableBandwidth: Math.round(availableBandwidth),
        quality,
        qualityLabel,
        bytesReceived,
        bytesSent,
        packetsLost,
        packetsReceived,
        packetsSent,
        timestamp: Date.now(),
      };
    } catch (error) {
      console.error('[MetricsService] Error collecting connection metrics:', error);
      return defaultMetrics;
    }
  }

  /**
   * Assess connection quality based on thresholds
   */
  private assessConnectionQuality(rtt: number, packetLoss: number, jitter: number): QualityLevel {
    const { connection } = THRESHOLDS;

    // Check for poor conditions first
    if (
      rtt > connection.rtt.fair ||
      packetLoss > connection.packetLoss.fair ||
      jitter > connection.jitter.fair
    ) {
      return 'poor';
    }

    // Check for fair conditions
    if (
      rtt > connection.rtt.good ||
      packetLoss > connection.packetLoss.good ||
      jitter > connection.jitter.good
    ) {
      return 'fair';
    }

    return 'good';
  }

  /**
   * Get human-readable connection quality label
   */
  private getConnectionQualityLabel(quality: QualityLevel): string {
    switch (quality) {
      case 'good': return 'Strong';
      case 'fair': return 'Fair';
      case 'poor': return 'Poor';
      default: return 'Connecting...';
    }
  }

  /**
   * Collect audio metrics from analyser
   */
  private collectAudioMetrics(): AudioMetrics {
    const defaultMetrics: AudioMetrics = {
      inputLevel: 0,
      peakLevel: 0,
      noiseFloor: 0,
      isClipping: false,
      isMuted: false,
      isSilent: true,
      quality: 'unknown',
      qualityLabel: 'No Audio',
      timestamp: Date.now(),
    };

    if (!this.analyser || !this.audioDataArray || !this.audioStream) {
      return defaultMetrics;
    }

    // Check if stream is muted
    const audioTrack = this.audioStream.getAudioTracks()[0];
    const isMuted = !audioTrack || !audioTrack.enabled;

    if (isMuted) {
      return {
        ...defaultMetrics,
        isMuted: true,
        quality: 'unknown',
        qualityLabel: 'Muted',
      };
    }

    // Get frequency data - use type assertion to handle ArrayBufferLike vs ArrayBuffer
    const dataArray = this.audioDataArray;
    this.analyser.getByteFrequencyData(dataArray as Uint8Array<ArrayBuffer>);

    // Calculate RMS level (more accurate than peak)
    let sum = 0;
    let max = 0;
    for (let i = 0; i < this.audioDataArray.length; i++) {
      const value = this.audioDataArray[i];
      sum += value * value;
      if (value > max) max = value;
    }
    const rms = Math.sqrt(sum / this.audioDataArray.length);

    // Normalize to 0-100
    const inputLevel = Math.round((rms / 255) * 100);

    // Update peak
    if (inputLevel > this.peakLevel) {
      this.peakLevel = inputLevel;
    }

    // Estimate noise floor (simplified - in production would use more sophisticated analysis)
    const noiseFloor = Math.min(inputLevel, 20);

    // Detect conditions
    const { audio } = THRESHOLDS;
    const isClipping = inputLevel > audio.clippingThreshold;
    const isSilent = inputLevel < audio.silenceThreshold;

    // Assess quality
    let quality: QualityLevel = 'good';
    let qualityLabel = 'Clean';

    if (isClipping) {
      quality = 'poor';
      qualityLabel = 'Clipping!';
    } else if (isSilent) {
      quality = 'unknown';
      qualityLabel = 'Silent';
    } else if (noiseFloor > audio.noiseFloorMax) {
      quality = 'fair';
      qualityLabel = 'Noisy';
    }

    return {
      inputLevel,
      peakLevel: this.peakLevel,
      noiseFloor,
      isClipping,
      isMuted,
      isSilent,
      quality,
      qualityLabel,
      timestamp: Date.now(),
    };
  }

  /**
   * Collect video metrics
   */
  private async collectVideoMetrics(): Promise<VideoMetrics> {
    const defaultMetrics: VideoMetrics = {
      width: 0,
      height: 0,
      frameRate: 0,
      droppedFrames: 0,
      totalFrames: 0,
      dropRate: 0,
      encodedBitrate: 0,
      quality: 'unknown',
      timestamp: Date.now(),
    };

    if (!this.peerConnection) {
      return defaultMetrics;
    }

    try {
      const stats = await this.peerConnection.getStats();
      let width = 0;
      let height = 0;
      let frameRate = 0;
      let framesDropped = 0;
      let framesSent = 0;

      stats.forEach(report => {
        if (report.type === 'outbound-rtp' && report.kind === 'video') {
          framesSent = report.framesEncoded || 0;
          framesDropped = report.framesDropped || 0;
        }

        if (report.type === 'media-source' && report.kind === 'video') {
          width = report.width || 0;
          height = report.height || 0;
          frameRate = report.framesPerSecond || 0;
        }
      });

      const totalFrames = framesSent + framesDropped;
      const dropRate = totalFrames > 0 ? (framesDropped / totalFrames) * 100 : 0;

      // Determine quality
      let quality: QualityLevel = 'good';
      if (dropRate > THRESHOLDS.video.dropRateFair) {
        quality = 'poor';
      } else if (dropRate > THRESHOLDS.video.dropRateGood) {
        quality = 'fair';
      }

      return {
        width,
        height,
        frameRate: Math.round(frameRate),
        droppedFrames: framesDropped,
        totalFrames,
        dropRate: Math.round(dropRate * 10) / 10,
        encodedBitrate: 0, // Would need time-based calculation
        quality,
        timestamp: Date.now(),
      };
    } catch (error) {
      console.error('[MetricsService] Error collecting video metrics:', error);
      return defaultMetrics;
    }
  }

  /**
   * Collect recording health metrics
   */
  private collectRecordingMetrics(): RecordingMetrics {
    // In a real implementation, this would integrate with the local recording system
    // For now, return healthy defaults
    return {
      bufferLevel: 100,
      bufferedDuration: 0,
      driftMs: 0,
      uploadProgress: 100,
      uploadSpeed: 0,
      pendingChunks: 0,
      isHealthy: true,
      healthLabel: 'Recording',
      timestamp: Date.now(),
    };
  }

  /**
   * Calculate overall session health
   */
  private calculateOverallHealth(
    connection: ConnectionMetrics,
    audio: AudioMetrics,
    video: VideoMetrics,
    _recording: RecordingMetrics
  ): QualityLevel {
    // Worst quality wins
    const qualities = [connection.quality, audio.quality, video.quality];

    if (qualities.includes('poor')) return 'poor';
    if (qualities.includes('fair')) return 'fair';
    if (qualities.includes('unknown')) return 'unknown';
    return 'good';
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    console.log('[MetricsService] Destroying...');

    this.stopPolling();

    if (this.peakResetInterval) {
      clearInterval(this.peakResetInterval);
      this.peakResetInterval = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.analyser = null;
    this.audioStream = null;
    this.peerConnection = null;
    this.callbacks.clear();
    this.lastMetrics = null;
    this.lastStats = null;
  }
}

// Export singleton instance
export const metricsService = new MetricsService();

// Also export class for testing
export { MetricsService };
